import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type CampaignClass,
  CampaignState,
  type GoogleAdsCreative,
  type GoogleAdsSmartCampaign,
} from '@feathr/blackbox';
import type { IWizardStepsCompleted } from '@feathr/components';
import {
  ActionBar,
  Button,
  ButtonValid,
  Step,
  Steps,
  useWizardState,
  Wizard,
} from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import { flattenErrors, useToggle } from '@feathr/hooks';
import type { ListResponse, Model } from '@feathr/rachis';

import SaveCampaignButton from '../../CampaignEditPage/SaveCampaignButton';
import type { ICampaignValidationErrors } from '../../CampaignSummary';
import AdStep, { useAdGroups } from './AdStep';
import AdTextStep, { validateAdTextStep } from './AdTextStep';
import BudgetStep, { validateBudgetStep } from './BudgetStep';
import GoalStep, { validateGoalStep } from './GoalStep';
import type { TGoogleAdsStepKey } from './GoogleAdsCampaignEdit.utils';
import { getGoogleAdsStepKeys, getReviewStepIndices } from './GoogleAdsCampaignEdit.utils';
import InformationStep, { validateInformationStep } from './InformationStep';
import KeywordsStep, { validateKeywordsStep } from './KeywordsStep';
import LocationStep, { validateLocationStep } from './LocationStep';
import { getSelectedTargetables } from './LocationStep/LocationStep';
import ReviewStep from './ReviewStep';

import * as styles from './GoogleAdsCampaignEdit.css';

interface IGoogleAdsCampaignEditProps {
  campaign: GoogleAdsSmartCampaign;
}

function GoogleAdsCampaignEdit({ campaign }: IGoogleAdsCampaignEditProps): JSX.Element {
  const { Creatives, Targetings, Targetables } = useStore();
  const { t } = useTranslation();
  const [showCallButton, toggleShowCallButton] = useToggle(!!campaign.get('phone_number'));

  const targetings = Targetings.list({
    filters: {
      _parent: campaign.id,
      is_archived__ne: true,
    },
    // set to 1000 to be safely more than the number of location targetings
    pagination: { page_size: 1000 },
  });

  // Search ads are creatives too, so this one list also carries every ad across a Search
  // campaign's ad groups. `useAdGroups` buckets it by `ad_group` rather than fetching per group.
  const creatives = Creatives.list({
    filters: {
      _parent: campaign.id,
      is_archived__ne: true,
    },
  }) as ListResponse<GoogleAdsCreative>;

  /*
   * Ad groups for the Search-only Ads step, fetched here rather than inside it so this
   * component can validate the same data the step renders — which drives the progress
   * rail, Next, and publish gating. Issues no request for a Smart campaign.
   */
  const { adGroupModels, ...adGroupsController } = useAdGroups({ campaign, creatives });

  // The wizard step layout is driven by the campaign class: Smart campaigns show
  // the Keywords and Ad Text steps; Search campaigns fold both into a single Ads
  // step. `campaign` is typed as GoogleAdsSmartCampaign so the shared wizard step
  // props keep their narrow type, so widen `_cls` back to the enum to compare.
  const campaignClass = campaign.get('_cls') as CampaignClass;
  const orderedKeys = getGoogleAdsStepKeys(campaignClass);
  const reviewStepIndices = getReviewStepIndices(campaignClass);
  const reviewIndex = orderedKeys.length - 1;

  /*
   * Flattened validation errors per step key; an empty array marks the step complete.
   *
   * Recomputed on call rather than built once during render. `useSteps` decides which step
   * to land on inside a MobX reaction that holds the closure from the render it was created
   * in, and it fires the instant the fetches land — before React can re-run the effect that
   * would replace that closure. A snapshot therefore described the campaign as it looked
   * with every collection still empty, which is why the wizard stopped at Location: no
   * targetings loaded reads the same as no locations chosen. Reading the models here means
   * the stale closure still produces a current answer.
   */
  function getErrorsByKey(): Record<TGoogleAdsStepKey, string[]> {
    return {
      information: flattenErrors(validateInformationStep(campaign)),
      location: validateLocationStep(targetings.models),
      keywords: flattenErrors(validateKeywordsStep(campaign)),
      adText: flattenErrors(validateAdTextStep(campaign, showCallButton, creatives)),
      ads: adGroupsController.getValidation().adsStepErrors,
      goal: flattenErrors(validateGoalStep(campaign)),
      budget: flattenErrors(validateBudgetStep(campaign)),
      review: [],
    };
  }

  const titlesByKey: Record<TGoogleAdsStepKey, string> = {
    information: t('Information'),
    location: t('Location'),
    keywords: t('Keywords'),
    adText: t('Ad Text'),
    ads: t('Ads'),
    goal: t('Goal'),
    budget: t('Budget'),
    review: t('Review'),
  };

  function getStepErrors(step: number): string[] {
    // If we're loading the wizard, return no errors
    if (step === -1) {
      return [];
    }
    return getErrorsByKey()[orderedKeys[step]] ?? [];
  }

  const getCompletedStepMap = (): IWizardStepsCompleted =>
    orderedKeys.reduce<IWizardStepsCompleted>((completed, _key, index) => {
      completed[index] = !getStepErrors(index).length;
      return completed;
    }, {});

  /*
   * Which step the wizard lands on is decided once, the first time this returns true, so it
   * has to mean "the data is here" rather than "nothing is in flight". A list that has not
   * started fetching reports `isPending: false` as well, and gating on that let the wizard
   * choose while every collection was still empty — it landed on Location, because a
   * campaign with no targetings loaded yet looks like a campaign with no locations.
   *
   * A failed fetch counts as arrived, or the wizard would never initialize and the step
   * body would stay blank. Every term reads its observable inside this function: it runs in
   * a MobX reaction, so a boolean computed during render would freeze at its first value.
   */
  const waitFor: () => boolean = () => {
    return (
      !campaign.isPending &&
      (targetings.hasLoadedOnce || targetings.isErrored) &&
      (creatives.hasLoadedOnce || creatives.isErrored) &&
      adGroupsController.hasLoaded()
    );
  };

  const { currentStep, completeStep, onNext, onPrev, onChangeStep } = useWizardState({
    getCompletedStepMap,
    waitFor,
  });

  /*
   * Which ad group the Review step asked to edit. Held here rather than in the Ads step
   * because that step unmounts when it isn't the current one, so it cannot be told before
   * it exists.
   */
  const [editAdGroupKey, setEditAdGroupKey] = useState<string | null>(null);

  const steps = (
    <Steps
      completed={campaign.get('state') === CampaignState.Published ? reviewIndex : completeStep}
      current={currentStep}
      onChange={handleChangeStep}
      usePortal={true}
    >
      {orderedKeys.map((key, index) => (
        <Step key={key} stepIndex={index} title={titlesByKey[key]} />
      ))}
    </Steps>
  );

  const currentStepHasErrors = !!getStepErrors(currentStep).length;
  const currentStepKey = orderedKeys[currentStep];

  // The request covers one visit to the Ads step, so returning later opens nothing.
  useEffect(() => {
    if (currentStepKey !== 'ads') {
      setEditAdGroupKey(null);
    }
  }, [currentStepKey]);

  function handleEditAdGroup(adGroupKey: string): void {
    setEditAdGroupKey(adGroupKey);
    onChangeStep(reviewStepIndices.ads);
  }

  async function handleChangeStep(step: number): Promise<void> {
    if (currentStep === 0) {
      await campaign.patchDirty();
    }
    onChangeStep(step);
  }

  async function handleNextStep(): Promise<void> {
    if (currentStep === 0) {
      await campaign.patchDirty();
    }
    onNext();
  }

  function validateButton(): ICampaignValidationErrors {
    const errorsByKey = getErrorsByKey();
    return orderedKeys.reduce<ICampaignValidationErrors>((errors, key) => {
      errors[key] = errorsByKey[key];
      return errors;
    }, {});
  }

  const childModels: Model[] = [
    ...getSelectedTargetables(targetings.models, Targetables),
    ...targetings.models,
    ...creatives.models,
  ];

  // Grandchildren, not children: `SaveCampaignButton` awaits every grandchild before any
  // child, and a Search ad can't be saved until the ad group it points at exists.
  const grandchildModels: Model[] = adGroupModels;

  const publishButton = (
    <SaveCampaignButton
      campaign={campaign}
      childModels={childModels}
      grandchildModels={grandchildModels}
      key={'publish'}
      name={'publish_or_stop'}
      shouldChangeState={true}
      showIcon={true}
      validate={validateButton}
    />
  );

  return (
    <Wizard className={styles.wizard} isFullWidth={true} layout={'horizontal'} steps={steps}>
      <ActionBar
        left={
          <SaveCampaignButton
            campaign={campaign}
            childModels={childModels}
            grandchildModels={grandchildModels}
            key={'save'}
            name={'save_changes'}
            validate={
              campaign.get('state') === CampaignState.Published ? validateButton : undefined
            }
          />
        }
        right={
          <>
            <Button disabled={currentStep === 0} onClick={onPrev}>
              {t('Previous')}
            </Button>
            {currentStep === reviewIndex && publishButton}
            {currentStep !== reviewIndex && (
              <ButtonValid
                disabled={currentStepHasErrors}
                errors={getStepErrors(currentStep)}
                name={'next_step'}
                onClick={handleNextStep}
              >
                {t('Next')}
              </ButtonValid>
            )}
          </>
        }
        usePortal={true}
      />
      <section className={styles.main}>
        {currentStepKey === 'information' && <InformationStep campaign={campaign} />}
        {currentStepKey === 'location' && (
          <LocationStep campaign={campaign} targetings={targetings.models} />
        )}
        {currentStepKey === 'keywords' && <KeywordsStep campaign={campaign} />}
        {currentStepKey === 'adText' && (
          <AdTextStep
            campaign={campaign}
            creatives={creatives}
            showCallButton={showCallButton}
            toggleShowCallButton={toggleShowCallButton}
          />
        )}
        {currentStepKey === 'ads' && (
          <AdStep
            {...adGroupsController}
            businessName={campaign.get('business_name')}
            editAdGroupKey={editAdGroupKey}
          />
        )}
        {currentStepKey === 'goal' && <GoalStep />}
        {currentStepKey === 'budget' && <BudgetStep campaign={campaign} />}
        {currentStepKey === 'review' && (
          <ReviewStep
            adGroups={adGroupsController.adGroups}
            campaign={campaign}
            creatives={creatives}
            onEditAdGroup={handleEditAdGroup}
            setCurrentStep={onChangeStep}
            stepIndices={reviewStepIndices}
            targetings={targetings.models}
          />
        )}
      </section>
    </Wizard>
  );
}

export default observer(GoogleAdsCampaignEdit);
